/**
 * ClassificationBadge Component
 *
 * Displays email classification result with confidence indicator.
 * Allows users to correct classifications by clicking on the badge.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { graphqlMutation } from '@business-assistant/shared/database/graphqlClient';

// =============================================================================
// TYPES
// =============================================================================

export type EmailClassification = 'work' | 'work_important' | 'work_routine' | 'advertisement' | 'spam' | 'unknown';

export interface ClassificationResult {
  classification: EmailClassification;
  confidence: number;
  reason: string;
  suggestedCompanyName?: string;
}

export interface ClassificationBadgeProps {
  result: ClassificationResult;
  emailId: string;
  emailSubject?: string;
  emailFrom?: string;
  emailBody?: string;
  onCorrect?: (
    emailId: string,
    original: EmailClassification,
    corrected: EmailClassification,
    note?: string
  ) => void;
  disabled?: boolean;
}

// =============================================================================
// DISPLAY CONFIG
// =============================================================================

const CLASSIFICATION_CONFIG: Record<
  EmailClassification,
  { bg: string; color: string; emoji: string; label: string }
> = {
  work: { bg: '#dbeafe', color: '#1d4ed8', emoji: '💼', label: 'Work' },
  work_important: { bg: '#fef2f2', color: '#dc2626', emoji: '🔴', label: 'Important' },
  work_routine: { bg: '#fefce8', color: '#ca8a04', emoji: '🟡', label: 'Routine' },
  advertisement: { bg: '#fef3c7', color: '#92400e', emoji: '📢', label: 'Ads' },
  spam: { bg: '#fee2e2', color: '#991b1b', emoji: '🚫', label: 'Spam' },
  unknown: { bg: '#f3f4f6', color: '#4b5563', emoji: '❓', label: 'Unclassified' },
};

const ALL_CLASSIFICATIONS: EmailClassification[] = ['work_important', 'work_routine', 'advertisement', 'spam'];

// =============================================================================
// COMPONENT
// =============================================================================

export function ClassificationBadge({
  result,
  emailId,
  emailSubject,
  emailFrom,
  emailBody,
  onCorrect,
  disabled = false,
}: ClassificationBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localClassification, setLocalClassification] = useState<EmailClassification | null>(null);
  const [pendingSelection, setPendingSelection] = useState<EmailClassification | null>(null);
  const [correctionNote, setCorrectionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentClassification = localClassification || result.classification;
  const config = CLASSIFICATION_CONFIG[currentClassification];
  const wasCorrected = localClassification !== null;

  // When user selects a classification, show the note input
  const handleSelect = (selected: EmailClassification) => {
    if (selected === currentClassification && !pendingSelection) {
      setIsOpen(false);
      return;
    }
    setPendingSelection(selected);
    // Focus the note input after state update
    setTimeout(() => noteInputRef.current?.focus(), 100);
  };

  // Cancel the pending correction
  const handleCancel = () => {
    setPendingSelection(null);
    setCorrectionNote('');
  };

  // Confirm and submit the correction
  const handleConfirm = async () => {
    if (!pendingSelection) return;

    setIsSubmitting(true);

    // Notify parent about correction
    if (onCorrect) {
      onCorrect(emailId, result.classification, pendingSelection, correctionNote || undefined);
    }

    // Submit correction to backend (centralized GraphQL)
    try {
      await graphqlMutation(`
        mutation CorrectClassification($input: CorrectClassificationInput!) {
          correctClassification(input: $input) {
            success
            error
          }
        }
      `, {
        input: {
          emailId,
          originalClassification: result.classification,
          correctedClassification: pendingSelection,
          emailSubject,
          emailFrom,
          emailBody: emailBody?.substring(0, 2000),
          correctionNote: correctionNote || undefined,
        },
      });

      // Update local state on success
      setLocalClassification(pendingSelection);
      setPendingSelection(null);
      setCorrectionNote('');
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to submit correction:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Badge Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setIsOpen(!isOpen);
        }}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px',
          background: config.bg,
          color: config.color,
          border: wasCorrected ? `2px solid ${config.color}` : 'none',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: 500,
          cursor: disabled ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
          transition: 'all 0.15s ease',
        }}
        title={`${result.reason}${wasCorrected ? ' (corrected)' : ''}\nConfidence: ${Math.round(result.confidence * 100)}%`}
      >
        <span>{config.emoji}</span>
        <span>{config.label}</span>
        {!disabled && (
          <ChevronDown
            size={10}
            style={{
              marginLeft: '2px',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.15s ease',
            }}
          />
        )}
        {wasCorrected && (
          <span
            style={{
              marginLeft: '2px',
              fontSize: '10px',
              opacity: 0.7,
            }}
          >
            ✓
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            zIndex: 1000,
            minWidth: pendingSelection ? '280px' : '140px',
          }}
        >
          <div style={{ padding: '4px' }}>
            <div
              style={{
                padding: '6px 8px',
                fontSize: '10px',
                color: '#6b7280',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Correct classification
            </div>

            {ALL_CLASSIFICATIONS.map((cls) => {
              const clsConfig = CLASSIFICATION_CONFIG[cls];
              const isSelected = cls === currentClassification && !pendingSelection;
              const isPending = cls === pendingSelection;

              return (
                <button
                  key={cls}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(cls);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '8px 10px',
                    background: isPending ? clsConfig.bg : isSelected ? '#f3f4f6' : 'transparent',
                    border: isPending ? `2px solid ${clsConfig.color}` : 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.1s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected && !isPending) {
                      e.currentTarget.style.background = '#f3f4f6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected && !isPending) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{clsConfig.emoji}</span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: '12px',
                      color: isPending ? clsConfig.color : isSelected ? '#374151' : '#374151',
                      fontWeight: isPending ? 600 : isSelected ? 500 : 400,
                    }}
                  >
                    {clsConfig.label}
                  </span>
                  {isSelected && <Check size={14} color="#6b7280" />}
                  {isPending && <Check size={14} color={clsConfig.color} />}
                </button>
              );
            })}
          </div>

          {/* Note input section - shown when a correction is pending */}
          {pendingSelection && (
            <div
              style={{
                padding: '12px',
                borderTop: '1px solid #e5e7eb',
                background: '#fefce8',
              }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#92400e',
                  marginBottom: '6px',
                }}
              >
                Note for AI (optional)
              </label>
              <textarea
                ref={noteInputRef}
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Explain why it's wrong and how to recognize similar emails..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '12px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '8px',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                  disabled={isSubmitting}
                  style={{
                    padding: '6px 12px',
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#374151',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirm();
                  }}
                  disabled={isSubmitting}
                  style={{
                    padding: '6px 12px',
                    background: '#2563eb',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 500,
                    color: 'white',
                    cursor: isSubmitting ? 'wait' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                  }}
                >
                  {isSubmitting ? 'Saving...' : 'Confirm'}
                </button>
              </div>
            </div>
          )}

          {/* Confidence indicator - hidden when note input is shown */}
          {!pendingSelection && (
            <div
              style={{
                padding: '8px 12px',
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '10px',
                  color: '#6b7280',
                }}
              >
                <span>AI Confidence</span>
                <span style={{ fontWeight: 600 }}>{Math.round(result.confidence * 100)}%</span>
              </div>
              <div
                style={{
                  marginTop: '4px',
                  height: '4px',
                  background: '#e5e7eb',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${result.confidence * 100}%`,
                    background:
                      result.confidence >= 0.8
                        ? '#22c55e'
                        : result.confidence >= 0.5
                        ? '#eab308'
                        : '#ef4444',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ClassificationBadge;
